import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Tag, StatCard } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Plane, ShoppingCart, Wallet, ClipboardCheck, CalendarDays,
  RefreshCw, ExternalLink, AlertTriangle, Check, X, Loader2,
  UserPlus, UserCheck, Factory, MessageSquareWarning, Users as UsersIcon,
  Trash2, History, Plus, ChevronDown, FileText, Image as ImageIcon, Paperclip, Download, Receipt,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { formatDate, humaniseDatesIn } from "@/lib/dateFormat";
import NewReviewDialog from "@/components/NewReviewDialog";

const KIND_META = {
  leave:          { label: "Leave",          tone: "blue",   Icon: Plane },
  purchase:       { label: "Purchase",       tone: "orange", Icon: ShoppingCart },
  reimbursement:  { label: "Reimbursement",  tone: "green",  Icon: Wallet },
  weekly_report:  { label: "Weekly report",  tone: "purple", Icon: CalendarDays },
  review:         { label: "Review",         tone: "yellow", Icon: ClipboardCheck },
  new_employee:   { label: "New employee",   tone: "blue",   Icon: UserPlus },
  new_client:     { label: "New client",     tone: "purple", Icon: UserCheck },
  new_vendor:     { label: "New vendor",     tone: "orange", Icon: Factory },
  recruitment:    { label: "Recruitment",    tone: "yellow", Icon: UsersIcon },
  grievance:      { label: "Grievance",      tone: "red",    Icon: MessageSquareWarning },
  task_review:    { label: "Task review",    tone: "purple", Icon: ClipboardCheck },
  quote:          { label: "Vendor quote",   tone: "orange", Icon: Receipt },
};

const fmtDate = (iso) => formatDate(iso);
function daysAgo(iso) {
  if (!iso) return null;
  try {
    const d = (Date.now() - new Date(iso).getTime()) / 86400000;
    return Math.max(0, Math.floor(d));
  } catch { return null; }
}

/* Unified approvals page — every pending item from leaves, weekly reports,
   purchases, reimbursements and reviews in a single inbox, tagged by kind.
   Default sort: most-overdue first (oldest created_at). */
export default function Approvals({ embedded = false }) {
  const { user, adminMode } = useAuth();
  const can = usePermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  // Feb 2026 — per-kind Pending + Approved counts (Task #3). Fetched once on
  // mount + after any decide/delete so the stat cards can render a divided
  // number ("pending on top / approved on bottom"). Kept separate from the
  // main inbox call so we can serve the counts even when a non-pending tab
  // is active without loading all approved rows.
  const [counts, setCounts] = useState({ pending: {}, approved: {} });
  // Phase 79 — accept ?type=<kind> query so dashboard "Needs you" purchase
  // items can deep-link into the pre-filtered Approvals view.
  const [urlParams] = useSearchParams();
  useEffect(() => {
    const t = urlParams.get("type");
    if (t) setFilter(t);
  }, [urlParams]);
  const [pending, setPending] = useState({}); // { "<kind>:<id>": "approve" | "reject" }
  const [selected, setSelected] = useState(() => new Set()); // "<kind>:<id>"
  const [bulkRunning, setBulkRunning] = useState(false);
  // Phase 60 — modal-driven rejection w/ mandatory note for every kind.
  const [rejectTarget, setRejectTarget] = useState(null); // { kind, id, title, isBulk? }
  const [rejectNote, setRejectNote] = useState("");
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  // Phase 63 — Status filter (Pending / Approved / Rejected / Deleted / All)
  // so admins can browse history, not just the live inbox. Default stays
  // "pending" so the inbox loads with zero behaviour change for non-history use.
  const [statusFilter, setStatusFilter] = useState("pending");
  // Phase 63 — Delete request flow. Admin-only soft-delete with optional reason
  // → chat broadcast to the submitter so they know not to act on it anymore.
  const [deleteTarget, setDeleteTarget] = useState(null); // { kind, id, title, isBulk?, count? }
  // Change #7 — inline review popup instead of navigating to /reviews/<id>
  const [reviewModal, setReviewModal] = useState(null);   // { id, loading, data }
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Admin (and adminMode ON) is the only role that can delete a request. The
  // server enforces 403 on this regardless; we hide the buttons for everyone
  // else to keep the UI clean.
  const canDelete = can("approvals_admin") && adminMode;
  const isHistoryView = statusFilter !== "pending";

  const STATUS_TABS = [
    { id: "pending",  label: "Pending"  },
    { id: "approved", label: "Approved" },
    { id: "rejected", label: "Rejected" },
    { id: "deleted",  label: "Deleted",  adminOnly: true },
    { id: "all",      label: "All"      },
  ];

  // Who can one-tap-decide each kind. Server enforces this too — this just
  // hides the buttons for users who'd get a 403.
  const canDecide = (kind, item) => {
    if (!user) return false;
    const role = user.role;
    // Phase 114 — Vendor quotes are Founder-only, deliberately excluding
    // Admin (unlike every other kind's isAdminEffective shortcut below).
    if (kind === "quote") return role === "founder" && adminMode;
    // Phase 141 — Weekly reports are Founder-only sign-off before a report
    // can go to a client, deliberately excluding the Admin shortcut below.
    if (kind === "weekly_report") return role === "founder" && adminMode;
    const isAdminEffective = can("approvals_admin") && adminMode;
    if (isAdminEffective) return true;
    if (kind === "leave") return can("leaves_edit");
    if (kind === "purchase") return can("finance_approve_purchases") || can("purchases_edit");
    if (kind === "reimbursement") return can("finance_edit");
    if (kind === "review") return role === "client_manager";
    // Phase 101 — Task Reviews: server computed `decidable` per row (Founder
    // or that specific project's Lead); mirror it here so the UI doesn't
    // show a button that would 403.
    if (kind === "task_review") return !!item?.decidable;
    if (["new_employee", "new_client", "new_vendor", "grievance"].includes(kind)) return role === "hr";
    if (kind === "recruitment") return role === "client_manager" || role === "hr";
    return false;
  };

  const loadCounts = async () => {
    try {
      const { data: c } = await api.get("/approvals/counts");
      setCounts(c);
    } catch { /* noop — cards keep last snapshot */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: c }] = await Promise.all([
        api.get("/approvals/inbox", { params: { status: statusFilter } }),
        api.get("/approvals/counts").catch(() => ({ data: { pending: {}, approved: {} } })),
      ]);
      setData(data);
      setCounts(c);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load"); }
    finally { setLoading(false); }
  };
  // Phase 63 — reload on status filter change. Clear current selection so we
  // don't accidentally bulk-act on rows that aren't visible anymore.
  useEffect(() => {
    load();
    setSelected(new Set());
  }, [statusFilter]);

  const decide = async (kind, id, decision, note = "") => {
    // Phase 60 — every rejection requires a written reason so the submitter
    // can see why their request was sent back. Open the reject modal instead
    // of running the decide call directly.
    if (decision === "reject" && !note) {
      const title = data?.items?.find((i) => i.kind === kind && i.id === id)?.title || `${kind} request`;
      setRejectTarget({ kind, id, title });
      setRejectNote("");
      return;
    }
    const key = `${kind}:${id}`;
    setPending((p) => ({ ...p, [key]: decision }));
    try {
      await api.post(`/approvals/${kind}/${id}/decide`, { decision, note: note || undefined });
      toast.success(`${KIND_META[kind]?.label || kind} ${decision === "approve" ? "approved" : "rejected"}${kind === "leave" ? " · email sent" : ""}`);
      // Optimistically drop the row from the inbox
      setData((d) => d ? {
        ...d,
        items: d.items.filter((i) => !(i.kind === kind && i.id === id)),
        total: d.total - 1,
        by_kind: { ...d.by_kind, [kind]: Math.max(0, (d.by_kind?.[kind] || 0) - 1) },
      } : d);
      // Feb 2026 · Task #3 — stat cards read from a separate /counts endpoint,
      // so we refresh those numbers after a decision to keep Pending/Approved
      // in sync without waiting for the user to switch tabs / hit Refresh.
      loadCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't ${decision}`);
    } finally {
      setPending((p) => { const n = { ...p }; delete n[key]; return n; });
    }
  };

  const confirmReject = async () => {
    const note = rejectNote.trim();
    if (note.length < 5) {
      toast.error("Please write a clear reason (at least 5 characters)");
      return;
    }
    setRejectSubmitting(true);
    try {
      if (rejectTarget.isBulk) {
        // Bulk reject path — drive the existing bulk-decide with the captured note.
        await runBulkDecide("reject", note);
      } else {
        await decide(rejectTarget.kind, rejectTarget.id, "reject", note);
      }
      setRejectTarget(null);
      setRejectNote("");
    } finally {
      setRejectSubmitting(false);
    }
  };

  // Phase 63 — Delete (soft) an approval request. Opens the confirm modal so
  // the admin can attach an optional reason that's shared with the submitter
  // via a #general chat broadcast.
  const deleteRequest = (kind, id, title) => {
    setDeleteTarget({ kind, id, title });
    setDeleteReason("");
  };
  const bulkDelete = () => {
    if (selected.size === 0) return;
    setDeleteTarget({ isBulk: true, count: selected.size });
    setDeleteReason("");
  };
  const confirmDelete = async () => {
    const reason = deleteReason.trim();
    setDeleteSubmitting(true);
    try {
      if (deleteTarget.isBulk) {
        const payload = {
          reason: reason || undefined,
          items: Array.from(selected).map((k) => {
            const [kind, id] = k.split(/:(.+)/);
            return { kind, id };
          }),
        };
        const { data: res } = await api.post("/approvals/bulk-delete", payload);
        const ok = res.succeeded || 0;
        const fail = res.failed || 0;
        if (fail === 0) toast.success(`Deleted ${ok} request${ok === 1 ? "" : "s"} · submitter${ok === 1 ? "" : "s"} notified`);
        else toast.warning(`${ok} deleted, ${fail} skipped`);
        const doneKeys = new Set(res.results.filter((r) => r.ok).map((r) => `${r.kind}:${r.id}`));
        setData((d) => d ? {
          ...d,
          items: d.items.filter((i) => !doneKeys.has(`${i.kind}:${i.id}`)),
          total: d.total - doneKeys.size,
          by_kind: (() => {
            const next = { ...(d.by_kind || {}) };
            res.results.filter((r) => r.ok).forEach((r) => {
              next[r.kind] = Math.max(0, (next[r.kind] || 0) - 1);
            });
            return next;
          })(),
        } : d);
        clearSelection();
      } else {
        await api.post(`/approvals/${deleteTarget.kind}/${deleteTarget.id}/delete`, { reason: reason || undefined });
        toast.success(`${KIND_META[deleteTarget.kind]?.label || "Request"} deleted · submitter notified in #general`);
        setData((d) => d ? {
          ...d,
          items: d.items.filter((i) => !(i.kind === deleteTarget.kind && i.id === deleteTarget.id)),
          total: d.total - 1,
          by_kind: { ...d.by_kind, [deleteTarget.kind]: Math.max(0, (d.by_kind?.[deleteTarget.kind] || 0) - 1) },
        } : d);
      }
      setDeleteTarget(null);
      setDeleteReason("");
      // Task #3 · keep stat-card counts fresh after any delete path.
      loadCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const items = useMemo(() => {
    if (!data) return [];
    return filter === "all" ? data.items : data.items.filter((i) => i.kind === filter);
  }, [data, filter]);

  // Reset selection if the filter changes — keep only rows still on screen.
  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(items.map((i) => `${i.kind}:${i.id}`));
      const next = new Set();
      prev.forEach((k) => { if (visible.has(k)) next.add(k); });
      return next;
    });
  }, [items]);

  const selectableItems = useMemo(() => items.filter((i) => canDecide(i.kind, i)), [items, user, adminMode]);
  const allVisibleSelected = selectableItems.length > 0 && selectableItems.every((i) => selected.has(`${i.kind}:${i.id}`));

  const toggleOne = (it) => {
    const k = `${it.kind}:${it.id}`;
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };
  const toggleAllVisible = () => {
    setSelected((s) => {
      const n = new Set(s);
      if (allVisibleSelected) {
        selectableItems.forEach((i) => n.delete(`${i.kind}:${i.id}`));
      } else {
        selectableItems.forEach((i) => n.add(`${i.kind}:${i.id}`));
      }
      return n;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const bulkDecide = async (decision) => {
    if (selected.size === 0) return;
    if (decision === "reject") {
      // Phase 60 — open the modal to capture a reason that applies to every
      // row in the selection (server requires `note` for every rejection).
      setRejectTarget({ isBulk: true, count: selected.size });
      setRejectNote("");
      return;
    }
    await runBulkDecide(decision, "");
  };

  const runBulkDecide = async (decision, note) => {
    if (selected.size === 0) return;
    const payload = {
      decision,
      note: note || undefined,
      items: Array.from(selected).map((k) => {
        const [kind, id] = k.split(/:(.+)/);
        return { kind, id };
      }),
    };
    setBulkRunning(true);
    try {
      const { data: res } = await api.post("/approvals/bulk-decide", payload);
      const ok = res.succeeded || 0;
      const fail = res.failed || 0;
      if (fail === 0) toast.success(`${decision === "approve" ? "Approved" : "Rejected"} ${ok} item${ok === 1 ? "" : "s"}`);
      else toast.warning(`${ok} ${decision === "approve" ? "approved" : "rejected"}, ${fail} skipped`);
      // Drop the rows that actually succeeded
      const doneKeys = new Set(res.results.filter((r) => r.ok).map((r) => `${r.kind}:${r.id}`));
      setData((d) => d ? {
        ...d,
        items: d.items.filter((i) => !doneKeys.has(`${i.kind}:${i.id}`)),
        total: d.total - doneKeys.size,
        by_kind: (() => {
          const next = { ...(d.by_kind || {}) };
          res.results.filter((r) => r.ok).forEach((r) => {
            next[r.kind] = Math.max(0, (next[r.kind] || 0) - 1);
          });
          return next;
        })(),
      } : d);
      clearSelection();
      loadCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk decide failed");
    } finally { setBulkRunning(false); }
  };

  return (
    <div className={embedded ? "fade-in" : "p-4 sm:p-8 max-w-6xl mx-auto fade-in"} data-testid="approvals-page">
      {/* Phase 82 — auto-opens on ?new=1 (triggered from Send-for-review launcher). */}
      <NewReviewDialog />
      {!embedded && (
        <PageHeader
          eyebrow="Inbox"
          title="Approvals"
          subtitle="Every pending decision in one place — leaves, reports, purchases, reimbursements and reviews."
          actions={
            <div className="flex items-center gap-2">
              {/* Phase 65 — Submit-launcher. The Approvals page is both an
                  inbox and the most natural place to start a new request. */}
              <NewRequestMenu />
              <Button variant="outline" className="rounded-sm" onClick={load} data-testid="approvals-refresh">
                <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>
          }
        />
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5 gap-3 mb-6">
        {Object.entries(KIND_META).map(([k, m]) => {
          const pendingN = counts?.pending?.[k] || 0;
          const approvedN = counts?.approved?.[k] || 0;
          const active = filter === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setFilter((cur) => (cur === k ? "all" : k))}
              data-testid={`stat-${k}`}
              className={`card-flat p-4 text-left w-full transition group hover:-translate-y-0.5 hover:border-slate-300 ${active ? "ring-1 ring-[#F54800] border-[#F54800]" : ""}`}
            >
              <div className="flex items-center gap-1.5 label-overline">
                <m.Icon size={11} className="text-slate-400" />
                {m.label}
              </div>
              <div className="mt-2 flex items-baseline gap-1.5" data-testid={`stat-${k}-pending`}>
                <span className={`font-display text-2xl sm:text-3xl font-bold ${pendingN > 0 ? "text-[#F54800]" : "text-slate-400"}`}>{pendingN}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">pending</span>
              </div>
              <div className="my-1.5 border-t border-slate-100" />
              <div className="flex items-baseline gap-1.5" data-testid={`stat-${k}-approved`}>
                <span className={`font-mono text-sm font-medium ${approvedN > 0 ? "text-emerald-700" : "text-slate-400"}`}>{approvedN}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">approved</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Phase 63 — Status filter (Pending / Approved / Rejected / Deleted / All).
          Lets admins browse history, not just the live inbox. Default is Pending. */}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="approvals-status-tabs">
        <History size={11} className="text-slate-400 mr-1" />
        {STATUS_TABS.filter((t) => !t.adminOnly || canDelete).map((t) => (
          <button
            key={t.id}
            onClick={() => setStatusFilter(t.id)}
            data-testid={`approvals-status-${t.id}`}
            className={`px-2.5 py-1 text-[11px] rounded-sm border transition-colors ${
              statusFilter === t.id
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="approvals-filter-chips">
        {[{ k: "all", label: "All" }, ...Object.entries(KIND_META).map(([k, m]) => ({ k, label: m.label }))].map(({ k, label }) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`approvals-chip-${k}`}
            className={`px-2.5 py-1 text-[11px] rounded-sm border transition-colors ${
              filter === k ? "bg-[#F54800] text-white border-[#F54800]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="card-flat overflow-hidden" data-testid="approvals-list">
        {items.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400">
            {loading
              ? "Loading…"
              : statusFilter === "pending"
                ? "Nothing pending — caught up. 🎉"
                : statusFilter === "deleted"
                  ? "No deleted requests."
                  : `No ${statusFilter} requests on record.`}
          </div>
        ) : (
          <>
            {/* Select-all header — only shown when there are decidable rows visible */}
            {selectableItems.length > 0 && (
              <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 flex items-center gap-3 text-xs">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  className="w-3.5 h-3.5 rounded-sm accent-[#F54800] cursor-pointer"
                  data-testid="approvals-select-all"
                  title={allVisibleSelected ? "Clear selection" : "Select all decidable on this view"}
                />
                <span className="text-slate-600">
                  {selected.size > 0 ? `${selected.size} selected` : `${selectableItems.length} you can decide`}
                </span>
                {selected.size > 0 && (
                  <button onClick={clearSelection} className="text-slate-400 hover:text-slate-700" data-testid="approvals-clear-selection">clear</button>
                )}
              </div>
            )}
            <ul className="divide-y divide-slate-100">
            {items.map((it) => {
              const m = KIND_META[it.kind] || KIND_META.review;
              const Icon = m.Icon;
              const age = daysAgo(it.created_at);
              const overdue = age != null && age >= 3;
              const key = `${it.kind}:${it.id}`;
              const isChecked = selected.has(key);
              const decidable = canDecide(it.kind, it);
              return (
                <li key={key} className={`px-4 py-3 flex items-start gap-3 transition-colors ${isChecked ? "bg-orange-50/40" : "hover:bg-slate-50"}`} data-testid={`approval-row-${it.id}`}>
                  {decidable ? (
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleOne(it)}
                      className="mt-1.5 w-3.5 h-3.5 rounded-sm accent-[#F54800] cursor-pointer shrink-0"
                      data-testid={`approval-check-${it.kind}-${it.id}`}
                    />
                  ) : (
                    <span className="w-3.5 h-3.5 mt-1.5 shrink-0" />
                  )}
                  <span className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 bg-${m.tone}-100 text-${m.tone}-700`}>
                    <Icon size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag tone={m.tone} data-testid={`approval-tag-${it.kind}`}>{m.label}</Tag>
                      <div className="font-medium text-sm text-slate-900 truncate" data-testid={`approval-title-${it.id}`}>{it.title}</div>
                      {it.amount != null && <span className="text-xs text-slate-500 font-mono">₹{Number(it.amount).toLocaleString("en-IN")}</span>}
                      {it.kind === "reimbursement" && (it.receipt_url || it.pre_approval_url) && (
                        <a href={it.receipt_url || it.pre_approval_url} target="_blank" rel="noreferrer" className="text-[10px] text-emerald-700 underline inline-flex items-center gap-0.5" data-testid="pre-approval-link">
                          receipt <ExternalLink size={9} />
                        </a>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{humaniseDatesIn(it.subtitle || it.user_name) || "—"}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5 inline-flex items-center gap-2 flex-wrap">
                      <span>{fmtDate(it.created_at)}</span>
                      {age != null && (
                        <span className={overdue && statusFilter === "pending" ? "text-red-600 font-semibold inline-flex items-center gap-0.5" : ""}>
                          {overdue && statusFilter === "pending" && <AlertTriangle size={9} />}{age}d ago
                        </span>
                      )}
                      {/* Phase 63 — show the underlying row status as a chip when we're in a history view. */}
                      {isHistoryView && it.row_status && (
                        <Tag tone={
                          ["approved", "active", "resolved", "hired"].includes(it.row_status) ? "green" :
                          ["rejected", "changes_requested", "dismissed"].includes(it.row_status) ? "red" :
                          it.row_status === "deleted" ? "slate" : "yellow"
                        } data-testid={`approval-row-status-${it.id}`}>{it.row_status}</Tag>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {/* Approve / Reject only available on Pending rows. History views are read-only. */}
                    {!isHistoryView && canDecide(it.kind, it) && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => decide(it.kind, it.id, "approve")}
                          disabled={!!pending[`${it.kind}:${it.id}`]}
                          className="h-7 px-2 rounded-sm bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`approve-${it.kind}-${it.id}`}
                          title="Approve"
                        >
                          {pending[`${it.kind}:${it.id}`] === "approve" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => decide(it.kind, it.id, "reject")}
                          disabled={!!pending[`${it.kind}:${it.id}`]}
                          className="h-7 px-2 rounded-sm border-red-200 text-red-700 hover:bg-red-50"
                          data-testid={`reject-${it.kind}-${it.id}`}
                          title="Reject"
                        >
                          {pending[`${it.kind}:${it.id}`] === "reject" ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                        </Button>
                      </>
                    )}
                    {/* Delete (admin-only) — available on any row except already-deleted ones. */}
                    {canDelete && it.row_status !== "deleted" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteRequest(it.kind, it.id, it.title)}
                        className="h-7 px-2 rounded-sm border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                        data-testid={`delete-${it.kind}-${it.id}`}
                        title="Delete request · notifies submitter"
                      >
                        <Trash2 size={12} />
                      </Button>
                    )}
                    {it.kind === "task_review" && (it.review_history || []).length > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="text-slate-400 hover:text-[#F54800] p-1"
                            data-testid={`task-review-history-${it.id}`}
                            title="Review history"
                          >
                            <History size={13} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-72 rounded-sm p-3 text-xs space-y-2 max-h-80 overflow-y-auto">
                          <div className="font-semibold text-slate-700 mb-1">Review history (nested rounds)</div>
                          {[...(it.review_history || [])].reverse().map((r, idx) => (
                            <div key={r.id || idx} className="border-l-2 border-slate-200 pl-2 py-0.5" data-testid={`task-review-round-${it.id}-${idx}`}>
                              <div className="text-slate-600">Round {(it.review_history.length - idx)} · submitted by {r.submitted_by_name || "—"}</div>
                              <div className={`font-medium ${r.status === "approved" ? "text-emerald-600" : r.status === "changes_requested" ? "text-red-600" : "text-amber-600"}`}>
                                {r.status === "pending" ? "Awaiting decision" : r.status === "approved" ? "Approved" : "Changes requested"}
                                {r.decided_by_name ? ` · by ${r.decided_by_name}` : ""}
                              </div>
                              {r.feedback && <div className="text-slate-500 italic mt-0.5">"{r.feedback}"</div>}
                            </div>
                          ))}
                        </PopoverContent>
                      </Popover>
                    )}
                    {it.kind === "review" ? (
                      <button
                        type="button"
                        onClick={async () => {
                          setReviewModal({ id: it.id, loading: true, data: null, itemKind: it.kind });
                          try {
                            const { data } = await api.get(`/reviews/${it.id}`);
                            setReviewModal({ id: it.id, loading: false, data, itemKind: it.kind });
                          } catch (e) {
                            toast.error("Failed to load review");
                            setReviewModal(null);
                          }
                        }}
                        className="text-xs text-[#F54800] hover:underline inline-flex items-center gap-0.5 ml-1"
                        data-testid={`approval-open-${it.id}`}
                      >
                        Open <ChevronDown size={11} />
                      </button>
                    ) : (
                      <Link to={it.link} className="text-xs text-[#F54800] hover:underline inline-flex items-center gap-0.5 ml-1" data-testid={`approval-open-${it.id}`}>
                        Open <ExternalLink size={11} />
                      </Link>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          </>
        )}
      </div>

      {/* Sticky bulk-decide bar — appears when ≥1 row selected */}
      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-sm shadow-2xl border border-slate-700 px-4 py-3 flex items-center gap-3 min-w-[420px]" data-testid="bulk-action-bar">
          <div className="text-sm font-semibold">{selected.size} selected</div>
          <div className="text-[10px] text-slate-400 hidden sm:block">{Array.from(selected).map((k) => k.split(":")[0]).reduce((acc, k) => { acc[k] = (acc[k] || 0) + 1; return acc; }, {})
            && Object.entries(Array.from(selected).map((k) => k.split(":")[0]).reduce((acc, k) => { acc[k] = (acc[k] || 0) + 1; return acc; }, {})).map(([k, n]) => `${n} ${k}`).join(" · ")}</div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" onClick={clearSelection} variant="ghost" className="h-8 rounded-sm text-slate-300 hover:text-white hover:bg-slate-800" data-testid="bulk-clear">
              Cancel
            </Button>
            {/* Bulk Delete — admin-only, available in every status view since the
                action just soft-flips status="deleted" on selected rows. */}
            {canDelete && (
              <Button size="sm" onClick={bulkDelete} disabled={bulkRunning} className="h-8 rounded-sm bg-slate-700 hover:bg-slate-800 text-white border border-slate-600" data-testid="bulk-delete">
                <Trash2 size={13} className="mr-1.5" />
                Delete {selected.size}
              </Button>
            )}
            {/* Approve / Reject only meaningful on Pending rows. */}
            {!isHistoryView && (
              <>
                <Button size="sm" onClick={() => bulkDecide("reject")} disabled={bulkRunning} className="h-8 rounded-sm bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-reject">
                  {bulkRunning ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <X size={13} className="mr-1.5" />}
                  Reject {selected.size}
                </Button>
                <Button size="sm" onClick={() => bulkDecide("approve")} disabled={bulkRunning} className="h-8 rounded-sm bg-green-600 hover:bg-green-700 text-white" data-testid="bulk-approve">
                  {bulkRunning ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Check size={13} className="mr-1.5" />}
                  Approve {selected.size}
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Phase 60 — Reject-with-reason modal. Note is mandatory for every kind. */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectNote(""); } }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <X size={16} className="text-red-600" />
              {rejectTarget?.isBulk
                ? `Reject ${rejectTarget?.count} item${rejectTarget?.count === 1 ? "" : "s"}`
                : "Reject this request"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {rejectTarget?.isBulk
                ? "The same reason will be attached to every selected item and shared with each submitter."
                : <>Tell <span className="text-slate-700 font-medium">{rejectTarget?.title || "the submitter"}</span> what to fix. They&apos;ll see this on their own Approvals inbox.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            <Textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={4}
              maxLength={300}
              autoFocus
              placeholder="e.g. The amount looks high — please attach a vendor quote and resubmit by Friday."
              className="rounded-sm text-sm"
              data-testid="reject-note-input"
            />
            <div className="text-[10px] text-slate-400 text-right">{rejectNote.length} / 300</div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setRejectTarget(null); setRejectNote(""); }} data-testid="reject-cancel">Cancel</Button>
            <Button
              onClick={confirmReject}
              disabled={rejectSubmitting || rejectNote.trim().length < 5}
              className="rounded-sm bg-red-600 hover:bg-red-700 text-white disabled:bg-slate-200 disabled:text-slate-400"
              data-testid="reject-confirm"
            >
              {rejectSubmitting ? <><Loader2 size={11} className="animate-spin mr-1.5" /> Rejecting…</> : <>Reject {rejectTarget?.isBulk ? `${rejectTarget?.count} items` : ""}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 63 — Delete-with-reason modal. Reason is optional. Confirmation
          spelled out so admins don't blow away requests by accident. */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteReason(""); } }}>
        <DialogContent className="rounded-sm max-w-md" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Trash2 size={16} className="text-slate-700" />
              {deleteTarget?.isBulk
                ? `Delete ${deleteTarget?.count} request${deleteTarget?.count === 1 ? "" : "s"}`
                : "Delete this request"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {deleteTarget?.isBulk
                ? <>Each submitter will be @-mentioned in <span className="font-semibold">#general</span> letting them know their request was deleted and no action is needed on their part.</>
                : <>The submitter for <span className="text-slate-700 font-medium">{deleteTarget?.title || "this request"}</span> will be @-mentioned in <span className="font-semibold">#general</span> with the reason below (if any), letting them know no action is needed on their part.</>}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            <Textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={3}
              maxLength={300}
              autoFocus
              placeholder="Optional — e.g. Duplicate request · superseded by PR #1248 · submitter cancelled in chat"
              className="rounded-sm text-sm"
              data-testid="delete-reason-input"
            />
            <div className="text-[10px] text-slate-400 text-right">{deleteReason.length} / 300</div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setDeleteTarget(null); setDeleteReason(""); }} data-testid="delete-cancel">Cancel</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteSubmitting}
              className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="delete-confirm"
            >
              {deleteSubmitting
                ? <><Loader2 size={11} className="animate-spin mr-1.5" /> Deleting…</>
                : <><Trash2 size={11} className="mr-1.5" />Delete {deleteTarget?.isBulk ? `${deleteTarget?.count} item${deleteTarget?.count === 1 ? "" : "s"}` : ""}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change #7 — Inline review popup: description, link, attachments, decide actions */}
      <Dialog open={!!reviewModal} onOpenChange={(o) => { if (!o) setReviewModal(null); }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="review-popup">
          {reviewModal?.loading ? (
            <div className="py-6 text-center text-xs text-slate-400"><Loader2 size={14} className="animate-spin inline mr-2" />Loading…</div>
          ) : reviewModal?.data ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display" data-testid="review-popup-title">{reviewModal.data.title}</DialogTitle>
                <DialogDescription className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                  <Tag tone={reviewModal.data.status === "approved" ? "green" : reviewModal.data.status === "changes_requested" ? "red" : "yellow"}>{reviewModal.data.status}</Tag>
                  <span>{reviewModal.data.created_by_name}</span>
                  {reviewModal.data.created_at && <span>· {formatDate(reviewModal.data.created_at)}</span>}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2 text-sm">
                {reviewModal.data.description && (
                  <div data-testid="review-popup-description">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Description</div>
                    <div className="text-slate-700 whitespace-pre-wrap">{reviewModal.data.description}</div>
                  </div>
                )}
                {reviewModal.data.link && (
                  <div data-testid="review-popup-link">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Link</div>
                    <a href={reviewModal.data.link} target="_blank" rel="noreferrer noopener" className="text-[#F54800] hover:underline inline-flex items-center gap-1 text-sm break-all">
                      {reviewModal.data.link.slice(0, 60)}{reviewModal.data.link.length > 60 ? "…" : ""} <ExternalLink size={11} />
                    </a>
                  </div>
                )}
                {(reviewModal.data.versions || []).length > 0 && (
                  <div data-testid="review-popup-attachments">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Attachments</div>
                    <div className="flex flex-wrap gap-2">
                      {(reviewModal.data.versions || []).map((v) => {
                        const kind = (v.file_kind || v.file_mime || v.file_name || "").toLowerCase();
                        const isImage = kind.includes("image") || /\.(png|jpe?g|webp|gif|heic)$/i.test(v.file_name || "");
                        const isPdf = kind.includes("pdf") || /\.pdf$/i.test(v.file_name || "");
                        const isDoc = /\.(docx?|xlsx?|pptx?)$/i.test(v.file_name || "");
                        const Icon = isImage ? ImageIcon : isPdf ? FileText : isDoc ? FileText : Paperclip;
                        const label = isImage ? "IMG" : isPdf ? "PDF" : isDoc ? "DOC" : "FILE";
                        // Feb 2026 · Image attachments render as icon-only chips
                        // per the batch spec — clicking opens the original file
                        // in a new tab (no inline preview / no filename clutter).
                        // Non-image files keep the fuller chip with filename so
                        // reviewers can tell PDFs apart at a glance.
                        if (isImage) {
                          return (
                            <a
                              key={v.id}
                              href={v.file_url}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="w-10 h-10 inline-flex items-center justify-center border border-slate-200 rounded-sm hover:bg-slate-50 hover:border-[#F54800] transition-colors"
                              data-testid={`review-popup-attachment-${v.id}`}
                              title={`${v.file_name} · opens in new tab`}
                              aria-label={`Open image ${v.file_name} in new tab`}
                            >
                              <Icon size={18} className="text-slate-500" />
                            </a>
                          );
                        }
                        return (
                          <a
                            key={v.id}
                            href={v.file_url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-2 border border-slate-200 rounded-sm px-2 py-1.5 hover:bg-slate-50 text-xs group"
                            data-testid={`review-popup-attachment-${v.id}`}
                            title={v.file_name}
                          >
                            <Icon size={16} className="text-slate-500 group-hover:text-[#F54800]" />
                            <div className="min-w-0">
                              <div className="text-[9px] text-slate-400 uppercase">{label} · v{v.n}</div>
                              <div className="text-slate-700 truncate max-w-[180px]">{v.file_name}</div>
                            </div>
                            <Download size={11} className="text-slate-400" />
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
                {!reviewModal.data.description && !reviewModal.data.link && (reviewModal.data.versions || []).length === 0 && (
                  <div className="text-xs text-slate-400 italic">No description, link, or attachments provided.</div>
                )}
              </div>
              <DialogFooter className="gap-2">
                {canDecide("review") && reviewModal.data.status === "pending" && (
                  <>
                    <Button
                      onClick={() => { decide("review", reviewModal.id, "approve"); setReviewModal(null); }}
                      className="rounded-sm bg-green-600 hover:bg-green-700 text-white"
                      data-testid="review-popup-approve"
                    >
                      <Check size={14} className="mr-1" /> Approve
                    </Button>
                    <Button
                      onClick={() => { setReviewModal(null); setRejectTarget({ kind: "review", id: reviewModal.id, title: reviewModal.data.title }); }}
                      variant="outline"
                      className="rounded-sm border-red-200 text-red-700 hover:bg-red-50"
                      data-testid="review-popup-reject"
                    >
                      <X size={14} className="mr-1" /> Reject
                    </Button>
                  </>
                )}
                {canDelete && (
                  <Button
                    onClick={() => { setReviewModal(null); setDeleteTarget({ kind: "review", id: reviewModal.id, title: reviewModal.data.title }); }}
                    variant="outline"
                    className="rounded-sm border-slate-200 text-slate-600"
                    data-testid="review-popup-delete"
                  >
                    <Trash2 size={14} className="mr-1" /> Delete
                  </Button>
                )}
                <Button variant="outline" className="rounded-sm ml-auto" onClick={() => setReviewModal(null)}>Close</Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Phase 65 — Submit-a-new-request launcher                                    */
/* -------------------------------------------------------------------------- */
/** Discoverability fix: employees previously had no idea WHERE on Analogy Hub
 *  they were supposed to submit a leave / purchase / weekly report etc. — they
 *  came to /approvals expecting a big "Create request" button and found only
 *  an inbox. This popover bridges that gap: one tap from /approvals → the
 *  right submission flow in its native module. Each option carries (a) a
 *  short blurb so first-time users know what fits the kind, and (b) a stable
 *  data-testid so e2e suites can drive the launcher deterministically. */
function NewRequestMenu() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // The catalog of submittable approval kinds. Role-gated entries (e.g.
  // recruitment / onboarding which are HR-led) are filtered before render so
  // employees only see paths they can actually walk.
  const REQUEST_KINDS = [
    {
      id: "leave",
      Icon: Plane,
      label: "Leave",
      blurb: "Plan time off · half-day or full-day",
      to: "/my-hr?tab=leaves&new=1",
      tone: "bg-blue-50 text-blue-700",
      roles: null, // anyone internal
    },
    {
      id: "purchase",
      Icon: ShoppingCart,
      label: "Purchase request",
      blurb: "Buy something for a project or the office",
      to: "/studio?tab=finance&sub=purchases&new=purchase",
      tone: "bg-orange-50 text-orange-700",
      roles: null,
    },
    {
      id: "reimbursement",
      Icon: Wallet,
      label: "Reimbursement",
      blurb: "Claim back something you already paid for",
      to: "/studio?tab=finance&sub=purchases&new=reimbursement",
      tone: "bg-green-50 text-green-700",
      roles: null,
    },
    {
      id: "weekly_report",
      Icon: CalendarDays,
      label: "Weekly report",
      blurb: "Submit a client-facing weekly summary for review",
      to: "/my-work?tab=weekly&new=1",
      tone: "bg-purple-50 text-purple-700",
      roles: ["admin", "founder", "client_manager"],
    },
    {
      id: "review",
      Icon: ClipboardCheck,
      label: "Send for review",
      blurb: "Push a project deliverable to the reviewer queue",
      to: "/my-work?tab=reviews&new=1",
      tone: "bg-yellow-50 text-yellow-700",
      roles: null,
    },
    {
      id: "grievance",
      Icon: MessageSquareWarning,
      label: "Grievance",
      blurb: "Anonymously raise a concern with HR + admin",
      to: "/my-hr?tab=grievances&new=1",
      tone: "bg-red-50 text-red-700",
      roles: null,
    },
  ];
  const allowed = REQUEST_KINDS.filter((k) => !k.roles || k.roles.includes(user?.role));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="new-request-btn">
          <Plus size={14} className="mr-1.5" /> New request <ChevronDown size={12} className="ml-1.5 opacity-80" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 rounded-sm p-1" data-testid="new-request-menu">
        <div className="px-3 py-2 border-b border-slate-100">
          <div className="label-overline">Submit a new request</div>
          <p className="text-[11px] text-slate-500 mt-0.5">Pick the kind below — we&apos;ll drop you on the right form.</p>
        </div>
        <ul className="py-1">
          {allowed.map((k) => (
            <li key={k.id}>
              <button
                type="button"
                onClick={() => { setOpen(false); navigate(k.to); }}
                className="w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-sm hover:bg-slate-50 group transition-colors"
                data-testid={`new-request-${k.id}`}
              >
                <span className={`shrink-0 w-7 h-7 rounded-sm inline-flex items-center justify-center ${k.tone}`}>
                  <k.Icon size={13} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-slate-800">{k.label}</span>
                  <span className="block text-[11px] text-slate-500 mt-0.5">{k.blurb}</span>
                </span>
                <ExternalLink size={10} className="text-slate-300 group-hover:text-slate-500 mt-1 shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
